import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import {
  exportCsv,
  formatDuration,
  formatTotalHours,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';
import type { Event } from '@/api/types';

/** Intervalo {start, end} em ISO durante o qual a máquina ficou com ignição ligada. */
interface IgnitionInterval {
  start: string;
  end: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

function formatDay(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
}

/** YYYY-MM-DD em hora local — chave de agrupamento dos trips por dia. */
function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface ColorBucket {
  /** classes do chip colorido aplicado ao total de horas do dia */
  chipBg: string;
  chipText: string;
  label: string;
}

/**
 * Cor do chip aplicada ao total de horas trabalhadas do dia.
 * - 0: neutro (não trabalhou)
 * - até 1h: amarelo
 * - até 2h: laranja
 * - até 5h: roxo
 * - acima: vermelho
 */
function colorForHours(ms: number): ColorBucket {
  if (!ms || ms <= 0)
    return { chipBg: 'bg-muted', chipText: 'text-muted-foreground', label: 'sem trabalho' };
  if (ms <= 1 * HOUR_MS)
    return { chipBg: 'bg-yellow-500', chipText: 'text-yellow-950', label: 'até 1h' };
  if (ms <= 2 * HOUR_MS)
    return { chipBg: 'bg-orange-500', chipText: 'text-white', label: '1h a 2h' };
  if (ms <= 5 * HOUR_MS)
    return { chipBg: 'bg-purple-500', chipText: 'text-white', label: '2h a 5h' };
  return { chipBg: 'bg-red-500', chipText: 'text-white', label: 'acima de 5h' };
}

/** Marcas da régua de 24h: 9 pontos, espaçamento de 3h. */
const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

/**
 * Distribui os intervalos de ignição em 24 buckets horários do dia
 * referente ao `dayKey` (data local). Cada bucket guarda quantos minutos
 * dessa hora a máquina ficou com ignição ligada (0 a 60).
 */
function buildHourBuckets(intervals: IgnitionInterval[], dayKey: string): number[] {
  const buckets = new Array(24).fill(0) as number[];
  const dayStart = new Date(`${dayKey}T00:00:00`).getTime();
  const dayEnd = dayStart + DAY_MS;
  for (const it of intervals) {
    const segStart = Math.max(new Date(it.start).getTime(), dayStart);
    const segEnd = Math.min(new Date(it.end).getTime(), dayEnd);
    if (segEnd <= segStart) continue;
    let cursor = segStart;
    while (cursor < segEnd) {
      const hour = new Date(cursor).getHours();
      const nextHourMs = new Date(cursor).setHours(hour + 1, 0, 0, 0);
      const stepEnd = Math.min(nextHourMs, segEnd);
      buckets[hour] += (stepEnd - cursor) / 60000;
      cursor = stepEnd;
    }
  }
  return buckets;
}

/**
 * A partir de eventos ignitionOn / ignitionOff retorna, por device, a
 * lista de intervalos em que a máquina ficou com ignição ligada. Edge
 * cases: ignitionOn duplicado é ignorado, ignitionOff sem on prévio é
 * ignorado, e um on sem off é fechado no fim do período pedido.
 */
function buildIgnitionIntervals(
  events: Event[],
  rangeEndIso: string,
): Map<number, IgnitionInterval[]> {
  const byDevice = new Map<number, Event[]>();
  for (const e of events) {
    if (e.deviceId == null) continue;
    if (e.type !== 'ignitionOn' && e.type !== 'ignitionOff') continue;
    const list = byDevice.get(e.deviceId);
    if (list) list.push(e);
    else byDevice.set(e.deviceId, [e]);
  }
  const result = new Map<number, IgnitionInterval[]>();
  for (const [deviceId, list] of byDevice) {
    list.sort((a, b) => a.serverTime.localeCompare(b.serverTime));
    const intervals: IgnitionInterval[] = [];
    let openStart: string | null = null;
    for (const e of list) {
      if (e.type === 'ignitionOn') {
        if (!openStart) openStart = e.serverTime;
      } else if (e.type === 'ignitionOff' && openStart) {
        intervals.push({ start: openStart, end: e.serverTime });
        openStart = null;
      }
    }
    if (openStart) intervals.push({ start: openStart, end: rangeEndIso });
    result.set(deviceId, intervals);
  }
  return result;
}

/**
 * Gráfico de área SVG cobrindo 0h-24h.
 * - Eixo X: hora do dia (0 a 24).
 * - Eixo Y (primary): minutos trabalhados em cada hora (0 a 60).
 * Cada hora vira um ponto da curva; a área sob a linha é preenchida.
 * Régua a cada 3h embaixo, grid horizontal a 30min e 60min.
 */
function DayBar({
  intervals,
  dayKey,
}: {
  intervals: IgnitionInterval[];
  dayKey: string;
}) {
  const buckets = buildHourBuckets(intervals, dayKey);

  const VB_W = 480;
  const PLOT_TOP = 6;
  const PLOT_H = 90;
  const PLOT_BOTTOM = PLOT_TOP + PLOT_H;
  const LABEL_Y = PLOT_BOTTOM + 22;
  const VB_H = LABEL_Y + 6;
  const LABEL_FONT_SIZE = 16;

  // Cada hora gera 2 pontos (início e fim) com o mesmo valor, criando
  // o efeito de "degrau" entre horas, que é mais honesto que interpolar
  // valores ausentes — o dado é por hora cheia.
  const yFor = (min: number) => PLOT_BOTTOM - (Math.min(60, min) / 60) * PLOT_H;
  const xFor = (h: number) => (h / 24) * VB_W;

  let pathPoints = `M 0 ${PLOT_BOTTOM}`;
  for (let h = 0; h < 24; h++) {
    const y = yFor(buckets[h] ?? 0);
    pathPoints += ` L ${xFor(h)} ${y} L ${xFor(h + 1)} ${y}`;
  }
  const linePath = pathPoints;
  const areaPath = `${pathPoints} L ${VB_W} ${PLOT_BOTTOM} Z`;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: VB_H, display: 'block' }}
    >
      {/* fundo do plot */}
      <rect
        x={0}
        y={PLOT_TOP}
        width={VB_W}
        height={PLOT_H}
        fill="hsl(var(--muted))"
        opacity={0.25}
        stroke="hsl(var(--border))"
        strokeWidth={1}
      />
      {/* grid horizontal (30 min e 60 min) */}
      <line
        x1={0}
        x2={VB_W}
        y1={yFor(30)}
        y2={yFor(30)}
        stroke="hsl(var(--border))"
        strokeWidth={0.5}
        strokeDasharray="2 3"
      />
      <line
        x1={0}
        x2={VB_W}
        y1={yFor(60)}
        y2={yFor(60)}
        stroke="hsl(var(--border))"
        strokeWidth={0.5}
        strokeDasharray="2 3"
      />
      {/* área preenchida sob a curva */}
      <path d={areaPath} fill="hsl(var(--primary))" opacity={0.25} />
      {/* linha do gráfico */}
      <path
        d={linePath}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {/* ticks + labels */}
      {TICK_HOURS.map((h) => {
        const x = xFor(h);
        return (
          <g key={h}>
            <line
              x1={x}
              x2={x}
              y1={PLOT_TOP}
              y2={PLOT_BOTTOM}
              stroke="hsl(var(--border))"
              strokeWidth={0.5}
            />
            <text
              x={x}
              y={LABEL_Y}
              fontSize={LABEL_FONT_SIZE}
              fontFamily="monospace"
              fill="hsl(var(--muted-foreground))"
              textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'}
            >
              {String(h).padStart(2, '0')}h
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function HoursReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const {
    deviceId,
    setDeviceId,
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    customError,
    executed,
    generate,
  } = useReportFilters('multi');

  // Período acima de 1 dia: pede ao Traccar uma linha por dia/máquina (daily=true).
  // Caso contrário, agrega tudo num resumo por máquina.
  const rangeMs = executed
    ? new Date(executed.range.to).getTime() - new Date(executed.range.from).getTime()
    : 0;
  const isDaily = rangeMs > DAY_MS;

  const query = useQuery({
    queryKey: ['report', 'hours', executed, isDaily],
    queryFn: () =>
      reportsApi.summary({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
        daily: isDaily,
      }),
    enabled: executed !== null,
  });

  // Em modo daily, pegamos eventos de ignitionOn/ignitionOff no mesmo
  // período pra montar a janela horária da máquina ligada. Em modo
  // agregado (1 dia), não precisa.
  const eventsQuery = useQuery({
    queryKey: ['report', 'hours-ignition', executed],
    queryFn: () =>
      reportsApi.events({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
        type: ['ignitionOn', 'ignitionOff'],
      }),
    enabled: executed !== null && isDaily,
  });

  const ignitionByDevice = useMemo(
    () =>
      buildIgnitionIntervals(
        eventsQuery.data ?? [],
        executed?.range.to ?? new Date().toISOString(),
      ),
    [eventsQuery.data, executed],
  );

  /** Filtra o subconjunto de intervalos que tocam o dia local. */
  const intervalsForDay = (deviceId: number, dayKey: string): IgnitionInterval[] => {
    const all = ignitionByDevice.get(deviceId);
    if (!all || !dayKey) return [];
    const dayStart = new Date(`${dayKey}T00:00:00`).getTime();
    const dayEnd = dayStart + DAY_MS;
    return all.filter((it) => {
      const s = new Date(it.start).getTime();
      const e = new Date(it.end).getTime();
      return e > dayStart && s < dayEnd;
    });
  };

  const rows = useMemo(() => {
    const list = query.data ?? [];
    if (isDaily) {
      return [...list].sort((a, b) => {
        const da = a.startTime ?? '';
        const db = b.startTime ?? '';
        if (da !== db) return db.localeCompare(da); // mais recente primeiro
        return (a.deviceName ?? '').localeCompare(b.deviceName ?? '', 'pt-BR');
      });
    }
    return [...list].sort((a, b) => (b.engineHours ?? 0) - (a.engineHours ?? 0));
  }, [query.data, isDaily]);

  const total = rows.reduce((sum, r) => sum + (r.engineHours ?? 0), 0);

  const handleExport = () => {
    if (!executed) return;
    const header = isDaily
      ? ['Data', 'Máquina', 'Horas trabalhadas', 'Horas (ms)', 'Faixa']
      : ['Máquina', 'Horas trabalhadas', 'Horas (ms)'];
    const body = rows.map((r) => {
      const machine = r.deviceName ?? devices.get(r.deviceId)?.name ?? '';
      const duration = r.engineHours != null ? formatDuration(r.engineHours) : '';
      const ms = r.engineHours ?? '';
      if (isDaily) {
        const bucket = colorForHours(r.engineHours ?? 0).label;
        return [formatDay(r.startTime), machine, duration, ms, bucket];
      }
      return [machine, duration, ms];
    });
    exportCsv(
      `horas_trabalhadas_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      header,
      body,
    );
  };

  return (
    <ReportShell
      title="Horas de Trabalho"
      description="Tempo total em operação por máquina no período. Em períodos maiores que um dia, mostra o detalhamento por dia e a janela horária."
    >
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        customError={customError}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && !query.isLoading && rows.length > 0 && (
        <div className="space-y-0.5 text-xs text-muted-foreground">
          <p>
            Total de Horas Trabalhadas no período: <span className="font-semibold text-foreground">{formatDuration(total)}</span>
          </p>
          <p>
            Total de Horas Trabalhadas no período: <span className="font-semibold text-foreground">{formatTotalHours(total)}</span>
          </p>
        </div>
      )}
      {isDaily && executed && !query.isLoading && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span className="font-semibold">Legenda:</span>
          <LegendChip color="bg-muted" label="sem trabalho" />
          <LegendChip color="bg-yellow-500" label="até 1h" />
          <LegendChip color="bg-orange-500" label="1h – 2h" />
          <LegendChip color="bg-purple-500" label="2h – 5h" />
          <LegendChip color="bg-red-500" label="acima de 5h" />
        </div>
      )}
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit={isDaily ? 'linhas (máquina · dia)' : 'máquinas'}
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {isDaily && <th className="px-4 py-2 font-semibold">Data</th>}
              <th className="px-4 py-2 font-semibold">Máquina</th>
              <th className="px-4 py-2 font-semibold">Horas trabalhadas</th>
              {isDaily && <th className="px-4 py-2 font-semibold">Janela 24h</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const dayKey = r.startTime ? localDayKey(r.startTime) : '';
              const intervalsOfDay = isDaily ? intervalsForDay(r.deviceId, dayKey) : [];
              const color = colorForHours(r.engineHours ?? 0);
              return (
                <tr
                  key={`${r.deviceId}-${r.startTime ?? i}`}
                  className="border-b border-border last:border-0 hover:bg-accent/40"
                >
                  {isDaily && (
                    <td className="px-4 py-2 font-mono text-xs">{formatDay(r.startTime)}</td>
                  )}
                  <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                  <td className="px-4 py-2">
                    {r.engineHours != null ? (
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 font-mono text-xs font-bold',
                          color.chipBg,
                          color.chipText,
                        )}
                      >
                        {formatDuration(r.engineHours)}
                      </span>
                    ) : (
                      <span className="font-mono text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  {isDaily && (
                    <td className="px-4 py-2" style={{ width: 520, minWidth: 520 }}>
                      <DayBar intervals={intervalsOfDay} dayKey={dayKey} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ReportTable>
    </ReportShell>
  );
}

function LegendChip({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('inline-block h-2.5 w-4 rounded-sm', color)} />
      {label}
    </span>
  );
}
